// TourismFXPage.js - Dólar Turismo & Viagens
import React, { useState, useEffect, useMemo } from 'react';
import { Header } from '../components/Header';
import Silk from '../components/Silk';
import FreeShopIndicator from '../components/dutyfree_indicator';
import TravelFXCard from '../components/travelcard';
import { useCurrencies } from '../components/useMarketData';

// ============================================
// REAL DATA SIMULATION (TURISMO MOCKS)
// ============================================
const cities = ['cuzco', 'nyc', 'paris', 'tokyo', 'buenosaires'];

// "The Real Economy Purchasing Power"
const FETCH_TRAVEL_INDEX = async () => {
  return [
    { destination: 'Nova York', flight: 2800, stay: 850, total: 3650, trend: 'up' },
    { destination: 'Lisboa', flight: 2900, stay: 550, total: 3450, trend: 'stable' },
    { destination: 'Buenos Aires', flight: 1200, stay: 380, total: 1580, trend: 'down' },
    { destination: 'Tóquio', flight: 4500, stay: 600, total: 5100, trend: 'up' },
  ];
};

// ============================================
// COMPONENTS
// ============================================
const TravelPowerIndex = ({ theme, data }) => (
  <div style={{
    background: theme.cardBg, borderRadius: '16px', padding: '1.5rem',
    border: `1px solid ${theme.border}`, backdropFilter: 'blur(10px)'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
      <div>
        <h3 style={{ fontSize: '1.3rem', fontWeight: 'bold', color: theme.textPrimary, margin: 0 }}>
          Índice de Poder de Viagem
        </h3>
        <p style={{ fontSize: '0.85rem', color: theme.textSecondary }}>
          Custo Real (Turismo): Voo (Ida/Volta) + 3 Dias Airbnb
        </p>
      </div>
      <div style={{ fontSize: '2rem' }}>✈️</div>
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {data.map((item, i) => (
        <div key={i} style={{ 
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem', background: 'rgba(255,255,255,0.03)',
          borderRadius: '8px', border: `1px solid ${theme.border}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
             <div style={{ 
               width: '4px', height: '40px', borderRadius: '2px', 
               background: item.trend === 'up' ? '#ef4444' : item.trend === 'down' ? '#4ade80' : '#fbbf24' 
             }} />
             <div>
               <div style={{ fontWeight: 'bold' }}>{item.destination}</div>
               <div style={{ fontSize: '0.8rem', color: theme.textSecondary }}>
                 Voo: R$ {item.flight} • Hospedagem: R$ {item.stay}
               </div>
             </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', fontFamily: 'monospace' }}>
              R$ {item.total}
            </div>
            <div style={{ fontSize: '0.75rem', color: theme.textSecondary }}>Total Est.</div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ============================================
// MAIN PAGE
// ============================================
export default function TourismFXPage() {
  const [isDark, setIsDark] = useState(true);
  const [travelData, setTravelData] = useState([]);
  const [turismoRate, setTurismoRate] = useState(null);

  const { data: apiCurrencies } = useCurrencies();

  // TICKER TAPE FORMATTING - THIS WAS MISSING
  const getFxTickers = useMemo(() => {
    if (!apiCurrencies || !Array.isArray(apiCurrencies)) return [];
    return apiCurrencies
      .filter(item => typeof item.last_quote === 'number' && item.last_quote !== 0) 
      .map(item => ({
        ticker: item.symbol,       
        name: item.name,           
        ult: 1 / item.last_quote,      
        var: item.rate * -1             
      }));
  }, [apiCurrencies]);

  const theme = {
    background: isDark ? 'rgba(5, 5, 5, 0.95)' : 'rgba(245, 245, 245, 0.95)',
    textPrimary: isDark ? '#fff' : '#111',
    textSecondary: isDark ? '#a1a1aa' : '#666',
    cardBg: isDark ? 'rgba(20, 20, 20, 0.6)' : 'rgba(255, 255, 255, 0.8)',
    border: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0,0,0,0.1)',
  };

  useEffect(() => {
    const loadData = async () => {
      const tr = await FETCH_TRAVEL_INDEX();
      setTravelData(tr);
      
      // Fetch Dólar Turismo directly
      try {
        const res = await fetch('https://economia.awesomeapi.com.br/json/last/USD-BRLT');
        const data = await res.json();
        setTurismoRate(data.USDBRLT);
      } catch (err) {
        console.error("Failed to fetch turismo", err);
      }
    };
    loadData();
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: isDark ? '#000' : '#f0f0f0', color: theme.textPrimary, position: 'relative' }}>
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}>
        <Silk speed={10} scale={3} color={isDark ? "#0ea5e9" : "#bae6fd"} noiseIntensity={isDark ? 3 : 1} />
      </div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        <Header 
          theme={theme} 
          isDark={isDark} 
          toggleTheme={() => setIsDark(!isDark)} 
          currency="USD"
          topTickers={getFxTickers} // FIXED HERE
        />

        <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem' }}>
          
          <div style={{ marginBottom: '4rem', marginTop: '2rem' }}>
            <h1 style={{ 
              fontSize: '3.5rem', fontWeight: '900', letterSpacing: '-2px', marginBottom: '0.5rem',
              background: 'linear-gradient(to right, #22d3ee, #0284c7)', 
              WebkitBackgroundClip: 'text', color: 'transparent'
            }}>
              Dólar Turismo & Viagens
            </h1>
            <p style={{ fontSize: '1.2rem', color: theme.textSecondary, maxWidth: '600px' }}>
              Poder de compra real, custos de viagem, e cotações de turismo (Dinheiro físico e cartões internacionais).
            </p>
            
            {turismoRate && (
              <div style={{ marginTop: '1.5rem', display: 'inline-block', background: theme.cardBg, padding: '1rem 2rem', borderRadius: '12px', border: `1px solid ${theme.border}` }}>
                <span style={{ fontSize: '0.9rem', color: theme.textSecondary, display: 'block' }}>Dólar Turismo Atual (AwesomeAPI)</span>
                <span style={{ fontSize: '2.5rem', fontWeight: 'bold', fontFamily: 'monospace' }}>R$ {parseFloat(turismoRate.bid).toFixed(2)}</span>
              </div>
            )}
          </div>

          <div style={{ marginBottom: '4rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px' }}>
            {cities.map(id => (
              <TravelFXCard key={id} cityId={id} theme={theme} />
            ))}
          </div>

          <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '2rem' }}>
            <TravelPowerIndex theme={theme} data={travelData} />
            <FreeShopIndicator theme={theme} />
          </section>

        </main>
      </div>
    </div>
  );
}